import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useLabOrders } from '@hooks/register/use-lab-orders';
import { useLocalize } from '@hooks/use-localize';
import { labOrdersSchema } from '@views/register/pwl/lab-orders.schema';
import type { ILabOrdersFormValues, ILabOrdersModel, ILabOrdersSubmitResult, IPwlLabStartResult, TPwlStartAction } from '@/types/history/lab-orders';
import OrdersPendingSection from '@views/register/pwl/lab-orders-sections/orders-pending-section';
import ReadyQuestSection from '@views/register/pwl/lab-orders-sections/ready-quest-section';
import ReadyPhlebotSection from '@views/register/pwl/lab-orders-sections/ready-phlebot-section';
import ChooseOptionSection from '@views/register/pwl/lab-orders-sections/choose-option-section';
import MentorOfferSection from './lab-orders-sections/mentor-offer-section';

const LabOrders = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError, startLabs, isStartingLabs } = useLabOrders(registrationId, editKey);

    if (isLoading) {
        return (
            <div className="row">
                <div className="col-xs-12">Loading&hellip;</div>
            </div>
        );
    }

    return (
        <div className='container-fluid'>
            {error && (
                <div className="row">
                    <div className="col-xs-12">
                        <div className="alert alert-danger">{error}</div>
                    </div>
                </div>
            )}

            <LabOrdersContent
                model={model}
                submit={submit}
                isSubmitting={isSubmitting || isStartingLabs}
                submitError={submitError}
                startLabs={startLabs}
            />
        </div>
    );
};

interface ILabOrdersContentProps {
    model: ILabOrdersModel;
    submit: (values: ILabOrdersFormValues) => Promise<ILabOrdersSubmitResult>;
    isSubmitting: boolean;
    submitError: string | null;
    startLabs: (action: TPwlStartAction) => Promise<IPwlLabStartResult>;
}

const LabOrdersContent = ({
    model,
    submit,
    isSubmitting,
    submitError,
    startLabs,
}: ILabOrdersContentProps) => {
    const t = useLocalize(model.languageData);

    const methods = useForm<ILabOrdersFormValues>({
        resolver: zodResolver(labOrdersSchema),
        defaultValues: {
            labChoice: '',
            connectToDonorIsDesired: '',
        },
    });

    const onValid = async (values: ILabOrdersFormValues) => {
        const result = await submit(values);

        if (!result.ok || !result.startAction) return;

        const started = await startLabs(result.startAction);

        // Starting the labs normally refreshes the model in place; a redirect only comes
        // back for the steps that are still Razor pages.
        if (started.redirectUrl) {
            window.location.assign(started.redirectUrl);
        }
    };

    // The four page states are mutually exclusive, mirroring the Razor if/else chain.
    const isReadyForQuest = !model.isPhlebot && model.hasLabRequisition;
    const isReadyForPhlebot = model.isPhlebot && model.hasLabRequisition;
    const isChoosingOption = !model.labOrdersPending && !isReadyForQuest && !isReadyForPhlebot;

    return (
        <div className="row row-margined">
            <div className="col-xs-12 col-sm-7 col-sm-offset-0">
                {model.isIE && (
                    <div className="alert alert-warning">
                        {t(
                            'Alert_BrowserRecommend',
                            'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                        )}
                    </div>
                )}

                {model.labOrdersFailed && (
                    <>
                        <h1>
                            {t('DashLabel_WeWereNotAbleToSuccessfullyRequestYourLabOrders', 'We were not able to successfully request your lab orders.')}
                        </h1>
                        <p>
                            {t('DashLabel_OftenThisResultsWhenTheAddressCannotBeValidated', 'Often, this results when the address cannot be validated.')}
                        </p>
                    </>
                )}

                {model.labOrdersPending && <OrdersPendingSection model={model} t={t} />}

                {isReadyForQuest && <ReadyQuestSection model={model} t={t} />}

                {isReadyForPhlebot && <ReadyPhlebotSection model={model} t={t} />}

                {isChoosingOption && (
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(onValid)}>
                            <ChooseOptionSection
                                model={model}
                                t={t}
                                isSubmitting={isSubmitting}
                            />

                            {submitError && (
                                <div className="alert alert-danger" style={{ marginTop: 20 }}>
                                    {submitError}
                                </div>
                            )}
                        </form>
                    </FormProvider>
                )}
            </div>

            <div className="col-xs-12 col-sm-5">
                <FormProvider {...methods}>
                    {<MentorOfferSection t={t} />}
                </FormProvider>
            </div>
        </div>
    );
};

export default LabOrders;
